/**
 * Offer Showcase Carousel - Premium Shopify Web Component
 * Vanilla Javascript, performance optimized with Intersection Observer and GPU animations.
 */

if (!customElements.get('offer-showcase-carousel')) {
  class OfferShowcaseCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.autoplayPaused = false;
      this.countdownIntervals = [];
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.offer-carousel__slide'));
      this.dots = Array.from(this.querySelectorAll('.offer-carousel__dot'));
      this.prevBtn = this.querySelector('.offer-carousel__nav-button--prev');
      this.nextBtn = this.querySelector('.offer-carousel__nav-button--next');
      this.track = this.querySelector('.offer-carousel__track');

      if (this.slides.length === 0) return;

      // Event Listeners
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
          this.stopAutoplay(); // Stop autoplay on manual dot interaction
        });
      });

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prev();
          this.stopAutoplay();
        } else if (e.key === 'ArrowRight') {
          this.next();
          this.stopAutoplay();
        }
      });

      // Pause autoplay on hover
      this.addEventListener('mouseenter', () => {
        this.autoplayPaused = true;
      });
      this.addEventListener('mouseleave', () => {
        this.autoplayPaused = false;
      });

      // Initialize
      this.initAutoplay();
      this.initCountdowns();
      this.updateNavHints();

      // Shopify Editor Integration
      this.addEventListener('shopify:block:select', (event) => {
        const slideIndex = parseInt(event.target.dataset.index);
        if (!isNaN(slideIndex)) {
          this.goToSlide(slideIndex);
          this.stopAutoplay();
        }
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.initAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.clearCountdowns();
    }

    goToSlide(index) {
      if (index === this.currentIndex) return;

      const previousIndex = this.currentIndex;
      
      // Handle circular bounds
      if (index >= this.slides.length) {
        this.currentIndex = 0;
      } else if (index < 0) {
        this.currentIndex = this.slides.length - 1;
      } else {
        this.currentIndex = index;
      }

      const direction = index > previousIndex ? 1 : -1;
      const prevSlide = this.slides[previousIndex];
      const activeSlide = this.slides[this.currentIndex];

      // Update Dots
      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === this.currentIndex);
      });

      // Update Section Background to match active slide's background property
      const slideBg = activeSlide.style.getPropertyValue('--slide-bg');
      this.style.setProperty('--slide-bg', slideBg);

      // Update Dynamic Arrows Labels
      this.updateNavHints();

      // Update ARIA attributes for screen readers
      this.slides.forEach((slide, idx) => {
        slide.setAttribute('aria-hidden', idx !== this.currentIndex);
      });

      // Native GPU-Accelerated Web Animations API (WAAPI) Engine
      if (typeof activeSlide.animate === 'function') {
        const slideOffset = direction * 70; // 70px directional offset
        const duration = 650;
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)'; // Super smooth fluid bezier

        // Lock slide states
        this.slides.forEach(s => s.classList.remove('is-active', 'is-leaving'));
        prevSlide.classList.add('is-leaving');
        activeSlide.classList.add('is-active');

        // Animate outgoing slide out smoothly in opposite direction
        const prevAnim = prevSlide.animate([
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
          { opacity: 0, transform: `translate3d(${-slideOffset}px, 0, 0) scale(0.97)` }
        ], {
          duration: duration,
          easing: easing,
          fill: 'forwards'
        });

        // Animate incoming slide in from direction
        const activeAnim = activeSlide.animate([
          { opacity: 0, transform: `translate3d(${slideOffset}px, 0, 0) scale(0.97)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
        ], {
          duration: duration,
          easing: easing,
          fill: 'forwards'
        });

        // Stagger textual elements entrance inside active slide
        const textElements = activeSlide.querySelectorAll(
          '.offer-carousel__sticker-tag, ' +
          '.offer-carousel__badge-tag, ' +
          '.offer-carousel__subheading, ' +
          '.offer-carousel__title, ' +
          '.offer-carousel__description, ' +
          '.offer-carousel__benefits-list, ' +
          '.offer-carousel__countdown-wrapper, ' +
          '.offer-carousel__cta'
        );

        textElements.forEach((el, idx) => {
          el.animate([
            { opacity: 0, transform: 'translate3d(0, 22px, 0)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' }
          ], {
            duration: 550,
            delay: 100 + (idx * 45), // Stagger delay per element
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'forwards'
          });
        });

        // Parallax image scale in
        const enteringImg = activeSlide.querySelector('.offer-carousel__img, .offer-carousel__bg-image');
        if (enteringImg) {
          enteringImg.animate([
            { transform: 'scale(1.06)' },
            { transform: 'scale(1)' }
          ], {
            duration: 950,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'forwards'
          });
        }

        activeAnim.onfinish = () => {
          prevSlide.classList.remove('is-leaving');
          // Clean up WAAPI fill styles to allow pure CSS state to take over
          prevAnim.cancel();
          activeAnim.cancel();
          this.isAnimating = false;
        };
      } else {
        // Fallback to CSS transitions
        this.slides.forEach((slide, idx) => {
          slide.classList.remove('is-active', 'is-leaving');
          if (idx === previousIndex) {
            slide.classList.add('is-leaving');
          }
        });
        activeSlide.classList.add('is-active');
        this.isAnimating = false;
      }
    }

    next() {
      this.goToSlide(this.currentIndex + 1);
    }

    prev() {
      this.goToSlide(this.currentIndex - 1);
    }

    updateNavHints() {
      if (this.slides.length <= 1) return;

      const prevLabelEl = this.prevBtn ? this.prevBtn.querySelector('.offer-carousel__nav-btn-label') : null;
      const nextLabelEl = this.nextBtn ? this.nextBtn.querySelector('.offer-carousel__nav-btn-label') : null;

      if (prevLabelEl) {
        const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
        const prevSlide = this.slides[prevIndex];
        prevLabelEl.textContent = prevSlide.getAttribute('data-nav-hint') || 'Ver más';
      }

      if (nextLabelEl) {
        const nextIndex = (this.currentIndex + 1) % this.slides.length;
        const nextSlide = this.slides[nextIndex];
        nextLabelEl.textContent = nextSlide.getAttribute('data-nav-hint') || 'Ver más';
      }

      // Animate label texts using Web Animations API
      [prevLabelEl, nextLabelEl].forEach((el) => {
        if (el && typeof el.animate === 'function') {
          el.animate([
            { opacity: 0, transform: 'translate3d(0, 5px, 0)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' }
          ], {
            duration: 400,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
          });
        }
      });
    }

    initAutoplay() {
      const isAutoplayEnabled = this.getAttribute('data-autoplay') === 'true';
      const speedAttr = this.getAttribute('data-autoplay-speed');
      const speed = speedAttr ? parseInt(speedAttr) : 6000;

      if (!isAutoplayEnabled || this.slides.length <= 1) return;

      this.stopAutoplay();

      // Performance: Only run timer if section is inside viewport
      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.startAutoplayTimer(speed);
          } else {
            this.stopAutoplayTimer();
          }
        });
      };

      this.observer = new IntersectionObserver(observerCallback, {
        threshold: 0.25, // At least 25% visible
      });
      this.observer.observe(this);
    }

    startAutoplayTimer(speed) {
      this.stopAutoplayTimer();
      this.autoplayTimer = setInterval(() => {
        if (!this.autoplayPaused) {
          this.next();
        }
      }, speed);
    }

    stopAutoplayTimer() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    stopAutoplay() {
      this.stopAutoplayTimer();
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    resumeAutoplay() {
      this.autoplayPaused = false;
    }

    pauseAutoplay() {
      this.autoplayPaused = true;
    }

    initCountdowns() {
      this.clearCountdowns();

      const countdownContainers = this.querySelectorAll('[data-countdown-date]');
      countdownContainers.forEach((container) => {
        const dateStr = container.getAttribute('data-countdown-date');
        if (!dateStr) return;

        // Try parsing string to Date object
        // Format AAAA-MM-DD HH:MM:SS or just AAAA-MM-DD
        const formattedDateStr = dateStr.trim().replace(/-/g, '/'); // better cross-browser parsing for old Safari
        const targetTime = new Date(formattedDateStr).getTime();

        if (isNaN(targetTime)) {
          console.warn(`Offer Showcase Carousel: Fecha de cuenta regresiva inválida "${dateStr}"`);
          container.style.display = 'none';
          return;
        }

        const daysEl = container.querySelector('.js-countdown-days');
        const hoursEl = container.querySelector('.js-countdown-hours');
        const minutesEl = container.querySelector('.js-countdown-minutes');
        const secondsEl = container.querySelector('.js-countdown-seconds');

        const updateTimer = () => {
          const now = new Date().getTime();
          const difference = targetTime - now;

          if (difference <= 0) {
            container.innerHTML = '<div class="offer-carousel__countdown-expired">¡Oferta expirada!</div>';
            return;
          }

          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
          if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
          if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
          if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
        };

        // Run once immediately
        updateTimer();
        
        // Interval ticking
        const intervalId = setInterval(updateTimer, 1000);
        this.countdownIntervals.push(intervalId);
      });
    }

    clearCountdowns() {
      this.countdownIntervals.forEach(interval => clearInterval(interval));
      this.countdownIntervals = [];
    }
  }

  customElements.define('offer-showcase-carousel', OfferShowcaseCarousel);
}
